import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, Skeleton, useTheme, useMediaQuery } from "@mui/material";
import { Donut, Legend } from "../../components/DonutComponent";
import {
    fetchSalesByCategoryGroup,
    fetchTotalBudgetByCategoryGroup,
} from "../../services/orderAnalytics.service";

const CATEGORY_KEY_MAP = {
    PRJ: "project",
    RET: "retail",
};

const CATEGORY_COLORS = {
    retail: "#07B8B2",
    project: "#525EB4",
};

const EMPTY_STATE = {
    actual: { project: 0, retail: 0 },
    budget: { project: 0, retail: 0 },
};

function SalesCategoryDonut({ dateParams, isDateRangeIncomplete }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [data, setData] = useState(EMPTY_STATE);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [hoveredCategory, setHoveredCategory] = useState(null);

    const { from, to } = dateParams || {};
    const year = from ? from.slice(0, 4) : null;

    useEffect(() => {
        if (isDateRangeIncomplete || !from || !to || !year) {
            setData(EMPTY_STATE);
            setIsLoading(false);
            return;
        }

        let isMounted = true;

        const loadData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const [salesList, budgetRes] = await Promise.all([
                    fetchSalesByCategoryGroup({ from, to }),
                    fetchTotalBudgetByCategoryGroup({ year }),
                ]);

                if (!isMounted) return;

                const actual = { project: 0, retail: 0 };
                salesList.forEach(item => {
                    const key = CATEGORY_KEY_MAP[item.id];
                    if (key) actual[key] += item.total || 0;
                });

                const budget = {
                    project: budgetRes?.PRJ || 0,
                    retail: budgetRes?.RET || 0,
                };

                setData({ actual, budget });
            } catch (err) {
                if (isMounted) {
                    console.error("SalesCategoryDonut error", err);
                    setError("Unable to load sales data");
                    setData(EMPTY_STATE);
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        loadData();

        return () => {
            isMounted = false;
        };
    }, [from, to, year, isDateRangeIncomplete]);

    const hasNoData = useMemo(() => {
        return (
            data.actual.project === 0 &&
            data.actual.retail === 0 &&
            data.budget.project === 0 &&
            data.budget.retail === 0
        );
    }, [data]);

    if (isLoading) return <Box sx={{ p: 2, bgcolor: "background.paper", minHeight: 320, display: "flex", justifyContent: "center", alignItems: "center", borderRadius: "20px" }}><Skeleton variant="circular" width={120} height={120} /></Box>;
    if (error) return <Box sx={{ p: 2, bgcolor: "background.paper", minHeight: 320, display: "flex", justifyContent: "center", alignItems: "center", borderRadius: "20px" }}><Typography sx={{ color: "error.main", fontSize: 12 }}>{error}</Typography></Box>;
    if (isDateRangeIncomplete) return <Box sx={{ p: 2, bgcolor: "background.paper", minHeight: 320, display: "flex", justifyContent: "center", alignItems: "center", borderRadius: "20px" }}><Typography sx={{ color: "warning.main", fontSize: 12 }}>Select both From & To dates</Typography></Box>;
    if (hasNoData) return <Box sx={{ p: 2, bgcolor: "background.paper", minHeight: 320, display: "flex", justifyContent: "center", alignItems: "center", borderRadius: "20px" }}><Typography sx={{ color: "text.secondary", fontSize: 12 }}>No sales data available</Typography></Box>;

    return (
        <Box
            sx={{
                p: { xs: 2.5, lg: 2 },
                borderRadius: "20px",
                bgcolor: "background.paper",
                boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                minHeight: { xs: 280, sm: 320 },
                transition: "all 0.2s ease",
                "&:hover": { transform: "translateY(-2px)" },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Typography sx={{ fontSize: { xs: 12, md: 14 }, color: "secondary.main" }}>
                    Sales - Category wise
                </Typography>

                <Box sx={{ display: "flex", gap: 2 }}>
                    <Legend 
                        color={CATEGORY_COLORS.project} 
                        label="Project" 
                        categoryKey="project"
                        hoveredCategory={hoveredCategory}
                        setHoveredCategory={setHoveredCategory}
                    />
                    <Legend 
                        color={CATEGORY_COLORS.retail} 
                        label="Retail" 
                        categoryKey="retail"
                        hoveredCategory={hoveredCategory}
                        setHoveredCategory={setHoveredCategory}
                    />
                </Box>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-around",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                <Donut
                    project={data.budget.project}
                    retail={data.budget.retail}
                    label="BUDGET"
                    isMobile={isMobile}
                    colors={CATEGORY_COLORS}
                    hoveredCategory={hoveredCategory}
                    setHoveredCategory={setHoveredCategory}
                />

                <Donut
                    project={data.actual.project}
                    retail={data.actual.retail}
                    label="ACTUAL"
                    isMobile={isMobile}
                    colors={CATEGORY_COLORS}
                    hoveredCategory={hoveredCategory}
                    setHoveredCategory={setHoveredCategory}
                />
            </Box>
        </Box>
    );
}

export default React.memo(SalesCategoryDonut);